import { RemoteRecordCleanUpAction } from '@client/common/redux/effects/createRemoteRecordCleanUpSaga';
import { RemoteRecord } from '@client/common/redux/RemoteRecord';
import { RemoteRecordData } from '@client/common/redux/RemoteRecordData';
import { AppState } from '@client/common/redux/store';
import { RMA_TYPE } from '@client/myAccount/rma/utils/rma';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CreateRmaBodyType,
  RmaDetailedType,
  RmaInvoiceAddressType,
  RmaInvoiceType,
  UpdateRmaBodyType,
} from 'b2b-common/core/account/api/rma';

export enum RMA_ATTACHMENT_STATUS {
  error = 'ERROR',
  uploaded = 'UPLOADED',
  uploading = 'UPLOADING',
}

export type RmaAttachmentUploadItemType = {
  error: ErrorType | null;
  fileName: string;
  id: string | null;
  mimeType: string;
  previewUrl: string;
  progress: number;
  status: RMA_ATTACHMENT_STATUS;
  tempId: string;
};

const initialState = {
  attachments: [] as RmaAttachmentUploadItemType[],
  cancel: RemoteRecordData.getDefault(),
  create: RemoteRecordData.getDefault(),
  rmaInvoices: {},
  rmaInvoicesAddresses: {},
  rmas: {},
  send: RemoteRecordData.getDefault(),
  update: RemoteRecordData.getDefault(),
};

export const rmaSlice = createSlice({
  name: 'rma',
  initialState,
  reducers: {
    // Creating RMA
    createRma: (state, action: PayloadAction<{
      data: CreateRmaBodyType;
      type: RMA_TYPE;
      isStandalone: boolean;
    }>) => {
      state.create = RemoteRecordData.init();
      return state;
    },
    createRmaFulfilled: (state) => {
      state.create = RemoteRecordData.fulfill({});
      return state;
    },
    createRmaRejected: (state, action: PayloadAction<ErrorType>) => {
      state.create = RemoteRecordData.reject(action.payload);
      return state;
    },
    clearRmaCreate: (state) => {
      state.create = initialState.create;
      return state;
    },
    // Fetching RMA
    fetchRma: (state, action: PayloadAction<{
      rmaId: string;
      debitorId?: string;
    }>) => {
      state.rmas[action.payload.rmaId] = RemoteRecordData.init();
      return state;
    },
    fetchRmaFulfilled: (state, action: PayloadAction<{
      rmaId: string;
      data: RmaDetailedType;
    }>) => {
      state.rmas[action.payload.rmaId] = RemoteRecordData.fulfill(action.payload.data);
      return state;
    },
    fetchRmaRejected: (state, action: PayloadAction<{
      rmaId: string;
      error: ErrorType;
    }>) => {
      state.rmas[action.payload.rmaId] = RemoteRecordData.reject(action.payload.error);
      return state;
    },
    fetchRmaCleanUp: (state, action: RemoteRecordCleanUpAction<RmaDetailedType>) => {
      state.rmas = action.payload.records;
      return state;
    },
    // Fetching RMA invoice address
    fetchRmaInvoiceAddress: (state, action: PayloadAction<string>) => {
      state.rmaInvoicesAddresses[action.payload] = RemoteRecordData.init();
      return state;
    },
    fetchRmaInvoiceAddressFulfilled: (state, action: PayloadAction<{
      rmaInvoiceId: string;
      data: RmaInvoiceAddressType;
    }>) => {
      state.rmaInvoicesAddresses[action.payload.rmaInvoiceId] = RemoteRecordData.fulfill(action.payload.data);
      return state;
    },
    fetchRmaInvoiceAddressRejected: (state, action: PayloadAction<{
      rmaInvoiceId: string;
      error: ErrorType;
    }>) => {
      state.rmaInvoicesAddresses[action.payload.rmaInvoiceId] = RemoteRecordData.reject(action.payload.error);
      return state;
    },
    fetchRmaInvoiceAddressCleanUp: (state, action: RemoteRecordCleanUpAction<RmaInvoiceAddressType>) => {
      state.rmaInvoicesAddresses = action.payload.records;
      return state;
    },
    // Fetching RMA invoice
    fetchRmaInvoice: (state, action: PayloadAction<{
      rmaInvoiceId: string,
      debitorId?: string;
    }>) => {
      state.rmaInvoices[action.payload.rmaInvoiceId] = RemoteRecordData.init();
      return state;
    },
    fetchRmaInvoiceFulfilled: (state, action: PayloadAction<{
      rmaInvoiceId: string;
      data: RmaInvoiceType;
    }>) => {
      state.rmaInvoices[action.payload.rmaInvoiceId] = RemoteRecordData.fulfill(action.payload.data);
      return state;
    },
    fetchRmaInvoiceRejected: (state, action: PayloadAction<{
      rmaInvoiceId: string;
      error: ErrorType;
    }>) => {
      state.rmaInvoices[action.payload.rmaInvoiceId] = RemoteRecordData.reject(action.payload.error);
      return state;
    },
    fetchRmaInvoiceCleanUp: (state, action: RemoteRecordCleanUpAction<RmaInvoiceType>) => {
      state.rmaInvoices = action.payload.records;
      return state;
    },
    clearRmaInvoices: (state) => {
      state.rmaInvoices = initialState.rmaInvoices;
      return state;
    },
    // Canceling RMA
    cancelRma: (state, action: PayloadAction<string>) => {
      state.cancel = RemoteRecordData.init();
      return state;
    },
    cancelRmaFulfilled: (state) => {
      state.cancel = RemoteRecordData.fulfill({});
      return state;
    },
    cancelRmaRejected: (state, action: PayloadAction<ErrorType>) => {
      state.cancel = RemoteRecordData.reject(action.payload);
      return state;
    },
    clearRmaCancel: (state) => {
      state.cancel = initialState.cancel;
      return state;
    },
    // Updating RMA
    updateRma: (state, action: PayloadAction<{
      rmaId: string;
      data: UpdateRmaBodyType;
      debitorId?: string;
    }>) => {
      state.update = RemoteRecordData.init();
      return state;
    },
    updateRmaFulfilled: (state) => {
      state.update = RemoteRecordData.fulfill({});
      return state;
    },
    updateRmaRejected: (state, action: PayloadAction<ErrorType>) => {
      state.update = RemoteRecordData.reject(action.payload);
      return state;
    },
    clearRmaUpdate: (state) => {
      state.update = initialState.update;
      return state;
    },
    // Sending RMA email
    sendRmaEmail: (state, action: PayloadAction<{
      debitorId: string;
      rmaId: string;
      email: string;
    }>) => {
      state.send = RemoteRecordData.init();
      return state;
    },
    sendRmaEmailFulfilled: (state) => {
      state.send = RemoteRecordData.fulfill({});
      return state;
    },
    sendRmaEmailRejected: (state, action: PayloadAction<ErrorType>) => {
      state.send = RemoteRecordData.reject(action.payload);
      return state;
    },
    clearRmaSendEmail: (state) => {
      state.send = initialState.send;
      return state;
    },
    // RMA attachments (upload during creation)
    uploadRmaAttachment: (state, action: PayloadAction<{
      tempId: string;
      file: File;
      previewUrl: string;
    }>) => {
      state.attachments.push({
        error: null,
        fileName: action.payload.file.name,
        id: null,
        mimeType: action.payload.file.type,
        previewUrl: action.payload.previewUrl,
        progress: 0,
        status: RMA_ATTACHMENT_STATUS.uploading,
        tempId: action.payload.tempId,
      });
      return state;
    },
    uploadRmaAttachmentProgress: (state, action: PayloadAction<{
      tempId: string;
      progress: number;
    }>) => {
      const item = state.attachments.find((attachment) => attachment.tempId === action.payload.tempId);
      if (item) {
        item.progress = action.payload.progress;
      }
      return state;
    },
    uploadRmaAttachmentFulfilled: (state, action: PayloadAction<{
      tempId: string;
      id: string;
    }>) => {
      const item = state.attachments.find((attachment) => attachment.tempId === action.payload.tempId);
      if (item) {
        item.status = RMA_ATTACHMENT_STATUS.uploaded;
        item.id = action.payload.id;
        item.progress = 100;
      }
      return state;
    },
    uploadRmaAttachmentRejected: (state, action: PayloadAction<{
      tempId: string;
      error: ErrorType;
    }>) => {
      const item = state.attachments.find((attachment) => attachment.tempId === action.payload.tempId);
      if (item) {
        item.status = RMA_ATTACHMENT_STATUS.error;
        item.error = action.payload.error;
      }
      return state;
    },
    addFailedRmaAttachment: (state, action: PayloadAction<{
      tempId: string;
      fileName: string;
      mimeType: string;
      previewUrl: string;
      error: ErrorType;
    }>) => {
      state.attachments.push({
        error: action.payload.error,
        fileName: action.payload.fileName,
        id: null,
        mimeType: action.payload.mimeType,
        previewUrl: action.payload.previewUrl,
        progress: 0,
        status: RMA_ATTACHMENT_STATUS.error,
        tempId: action.payload.tempId,
      });
      return state;
    },
    removeRmaAttachment: (state, action: PayloadAction<{
      tempId: string;
      id: string | null;
    }>) => {
      state.attachments = state.attachments.filter((attachment) => attachment.tempId !== action.payload.tempId);
      return state;
    },
    clearRmaAttachments: (state) => {
      state.attachments = initialState.attachments;
      return state;
    },
    clearAccountRma: () => initialState,
  },
});

export const {
  createRma,
  createRmaFulfilled,
  createRmaRejected,
  clearRmaCreate,
  fetchRma,
  fetchRmaFulfilled,
  fetchRmaRejected,
  fetchRmaCleanUp,
  fetchRmaInvoiceAddress,
  fetchRmaInvoiceAddressFulfilled,
  fetchRmaInvoiceAddressRejected,
  fetchRmaInvoiceAddressCleanUp,
  fetchRmaInvoice,
  fetchRmaInvoiceFulfilled,
  fetchRmaInvoiceRejected,
  fetchRmaInvoiceCleanUp,
  clearRmaInvoices,
  cancelRma,
  cancelRmaFulfilled,
  cancelRmaRejected,
  clearRmaCancel,
  updateRma,
  updateRmaFulfilled,
  updateRmaRejected,
  clearRmaUpdate,
  sendRmaEmail,
  sendRmaEmailFulfilled,
  sendRmaEmailRejected,
  clearRmaSendEmail,
  uploadRmaAttachment,
  uploadRmaAttachmentProgress,
  uploadRmaAttachmentFulfilled,
  uploadRmaAttachmentRejected,
  addFailedRmaAttachment,
  removeRmaAttachment,
  clearRmaAttachments,
  clearAccountRma,
} = rmaSlice.actions;

const getState = (state: AppState) => state.myAccount.rma.rma;

export const selectRmaRecords = createSelector(
  [getState],
  (state) => state.rmas,
);

export const selectRma = createSelector(
  [getState, (state, rmaId: string) => rmaId],
  (state, rmaId) => new RemoteRecord<RmaDetailedType>(state.rmas[rmaId]),
);

export const selectRmaInvoiceRecords = createSelector(
  [getState],
  (state) => state.rmaInvoices,
);

export const selectRmaInvoice = createSelector(
  [getState, (state, rmaInvoiceId: string) => rmaInvoiceId],
  (state, rmaInvoiceId) => new RemoteRecord<RmaInvoiceType>(state.rmaInvoices[rmaInvoiceId]),
);

export const selectRmaInvoiceAddressRecords = createSelector(
  [getState],
  (state) => state.rmaInvoicesAddresses,
);

export const selectRmaInvoiceAddress = createSelector(
  [getState, (state, rmaInvoiceId: string) => rmaInvoiceId],
  (state, rmaInvoiceId) => new RemoteRecord<RmaInvoiceAddressType>(state.rmaInvoicesAddresses[rmaInvoiceId]),
);

export const selectCancelRma = createSelector(
  getState,
  state => new RemoteRecord(state.cancel),
);

export const selectCreateRma = createSelector(
  getState,
  state => new RemoteRecord(state.create),
);

export const selectUpdateRma = createSelector(
  getState,
  state => new RemoteRecord(state.update),
);

export const selectSendRmaEmail = createSelector(
  getState,
  state => new RemoteRecord(state.send),
);

export const selectRmaAttachments = createSelector(
  getState,
  state => state.attachments,
);
